import { PlatformModule } from '@/utils/enums';
import {
  ADD_JOB_POST_ROUTE,
  ATTENDANCE_ROUTE,
  ATTENDANCE_SETTING_ROUTE,
  EDIT_JOB_POST_ROUTE,
  EMPLOYEE_ALLOWANCE_ROUTE,
  EMPLOYEE_ATTENDANCE_ROUTE,
  EMPLOYEE_LEAVE_ROUTE,
  EMPLOYEE_OVERTIME_ROUTE,
  EMPLOYEE_PAYROLL_COMPONENT_ROUTE,
  EMPLOYEE_PAYROLL_DETAILS_ROUTE,
  EMPLOYEE_PAYROLL_ROUTE,
  EMPLOYEE_PAYROLL_SALARY_ROUTE,
  EMPLOYEE_PROMOTION_HISTORY_ROUTE,
  JOB_APPLICATIONS_ROUTE,
  JOB_POST_DETAILS_ROUTE,
  LEAVE_TYPES_ROUTE,
  LEAVES_ROUTE,
  OVERTIME_ROUTE,
  PAYROLL_DETAILS_ROUTE,
  PAYROLL_ROUTE,
  PAYROLL_SETTINGS_ROUTE,
  PAYROLL_SUMMARY_ROUTE,
  PROFILE_ALLOWANCE_ROUTE,
  PROFILE_ATTENDANCE_ROUTE,
  PROFILE_LEAVE_ROUTE,
  PROFILE_OVERTIME_ROUTE,
  PROFILE_PAYROLL_ROUTE,
  PROJECT_DETAILS_ROUTE,
  PROJECTS_ROUTE,
  RECRUITMENT_ROUTE,
  CHAT_ROUTE,
  CHAT_CONVERSATION_ROUTE,
  FILES_ROUTE,
  FILES_FOLDER_ROUTE,
} from '@/router/routes';

/**
 * Product modules the platform admin can switch on per organization. Everything
 * not listed here (people, org structure, holidays, settings, the reports library,
 * export templates, the knowledge base) is core and always available; reports and
 * export templates only offer the data of licensed modules.
 */
export interface PlatformModuleMeta {
  key: PlatformModule;
  label: string;
  description: string;
  /** primeicons class */
  icon: string;
  /** dashboard tint token, see dashboard.css `.tint-*` */
  tint: string;
}

export const PLATFORM_MODULES: PlatformModuleMeta[] = [
  { key: PlatformModule.attendance, label: 'Attendance', description: 'Check-ins, shifts, schedules and attendance analytics.', icon: 'pi-clock', tint: 'blue' },
  { key: PlatformModule.leave, label: 'Leave', description: 'Leave requests, balances, policies and leave types.', icon: 'pi-calendar', tint: 'purple' },
  { key: PlatformModule.payroll, label: 'Payroll', description: 'Salaries, payroll periods, components and overtime.', icon: 'pi-wallet', tint: 'amber' },
  { key: PlatformModule.projectManagement, label: 'Project management', description: 'Clients, projects, milestones, invoices and transactions.', icon: 'pi-briefcase', tint: 'teal' },
  { key: PlatformModule.recruitment, label: 'Recruitment', description: 'Job posts, the public careers form and the applicant pipeline.', icon: 'pi-user-plus', tint: 'green' },
  { key: PlatformModule.chat, label: 'Chat', description: 'Direct messages, groups, the General channel, file sharing and message notifications.', icon: 'pi-comments', tint: 'green' },
  { key: PlatformModule.files, label: 'Files', description: 'Shared folders and files for the whole organization, admin-only folders and a storage quota.', icon: 'pi-folder-open', tint: 'blue' },
];

export const ALL_PLATFORM_MODULE_KEYS: PlatformModule[] = PLATFORM_MODULES.map((module) => module.key);

export const platformModuleMeta = (key: PlatformModule | string): PlatformModuleMeta =>
  PLATFORM_MODULES.find((module) => module.key === key) ?? { key: key as PlatformModule, label: String(key), description: '', icon: 'pi-box', tint: 'gray' };

/** Sent by the API as `error.errorCode` when a request hits a module the tenant lacks. */
export const MODULE_DISABLED_ERROR_CODE = 'MODULE_DISABLED';

type OrganizationLike = { enabledModules?: unknown } | null | undefined;

/**
 * The modules an organization may use, in catalog order. An organization payload
 * without the field (older session data) counts as fully licensed; the API is the
 * authority and refuses anything the tenant is not licensed for.
 */
export const enabledModulesOf = (organization: OrganizationLike): PlatformModule[] => {
  const raw = organization?.enabledModules;
  if (!Array.isArray(raw)) return [...ALL_PLATFORM_MODULE_KEYS];
  return ALL_PLATFORM_MODULE_KEYS.filter((key) => raw.includes(key));
};

export const isModuleEnabled = (source: OrganizationLike | PlatformModule[], key: PlatformModule): boolean => {
  const modules = Array.isArray(source) ? source : enabledModulesOf(source);
  return modules.includes(key);
};

/**
 * Routes that belong to a module. Anything missing here is core. Public pages that
 * other organizations' visitors open (the careers form) are deliberately absent:
 * they are checked by the API against the organization that owns the record.
 */
export const ROUTE_MODULES: Record<string, PlatformModule> = {
  [ATTENDANCE_ROUTE]: PlatformModule.attendance,
  [ATTENDANCE_SETTING_ROUTE]: PlatformModule.attendance,
  [EMPLOYEE_ATTENDANCE_ROUTE]: PlatformModule.attendance,
  [PROFILE_ATTENDANCE_ROUTE]: PlatformModule.attendance,

  [LEAVES_ROUTE]: PlatformModule.leave,
  [LEAVE_TYPES_ROUTE]: PlatformModule.leave,
  [EMPLOYEE_LEAVE_ROUTE]: PlatformModule.leave,
  [PROFILE_LEAVE_ROUTE]: PlatformModule.leave,

  [PAYROLL_ROUTE]: PlatformModule.payroll,
  [PAYROLL_SUMMARY_ROUTE]: PlatformModule.payroll,
  [PAYROLL_DETAILS_ROUTE]: PlatformModule.payroll,
  [PAYROLL_SETTINGS_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_PAYROLL_DETAILS_ROUTE]: PlatformModule.payroll,
  [OVERTIME_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_PAYROLL_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_PAYROLL_SALARY_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_PAYROLL_COMPONENT_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_PROMOTION_HISTORY_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_ALLOWANCE_ROUTE]: PlatformModule.payroll,
  [EMPLOYEE_OVERTIME_ROUTE]: PlatformModule.payroll,
  [PROFILE_PAYROLL_ROUTE]: PlatformModule.payroll,
  [PROFILE_ALLOWANCE_ROUTE]: PlatformModule.payroll,
  [PROFILE_OVERTIME_ROUTE]: PlatformModule.payroll,

  [PROJECTS_ROUTE]: PlatformModule.projectManagement,
  [PROJECT_DETAILS_ROUTE]: PlatformModule.projectManagement,

  [RECRUITMENT_ROUTE]: PlatformModule.recruitment,
  [ADD_JOB_POST_ROUTE]: PlatformModule.recruitment,
  [EDIT_JOB_POST_ROUTE]: PlatformModule.recruitment,
  [JOB_POST_DETAILS_ROUTE]: PlatformModule.recruitment,
  [JOB_APPLICATIONS_ROUTE]: PlatformModule.recruitment,

  [CHAT_ROUTE]: PlatformModule.chat,
  [CHAT_CONVERSATION_ROUTE]: PlatformModule.chat,

  [FILES_ROUTE]: PlatformModule.files,
  [FILES_FOLDER_ROUTE]: PlatformModule.files,
};

export const moduleForRoute = (routeName: unknown): PlatformModule | null =>
  (routeName ? ROUTE_MODULES[String(routeName)] : null) ?? null;

/** Which licence each report-library module needs; `employees` is core. */
export const REPORT_MODULE_LICENCE: Record<string, PlatformModule | null> = {
  employees: null,
  attendance: PlatformModule.attendance,
  leave: PlatformModule.leave,
  payroll: PlatformModule.payroll,
  recruitment: PlatformModule.recruitment,
  projects: PlatformModule.projectManagement,
};

export const isReportModuleEnabled = (reportModuleKey: string, modules: PlatformModule[]): boolean => {
  const licence = REPORT_MODULE_LICENCE[reportModuleKey] ?? null;
  return licence ? modules.includes(licence) : true;
};
